/**
 * `BrandProvider` / `useBrand()` — layer 3 of the SPA brand scheme.
 *
 * Mounted at/above `ThemeProvider` in apps/web/src/App.tsx, which is the
 * outermost data provider and therefore the one level that covers the login
 * page, the share view, AND the app shell in one place. It has no data
 * dependencies of its own (no session, no tenant), so it can sit that high.
 *
 * The snapshot is CONSTANT — baked into index.html at build time and read
 * synchronously from `../client.js`. This used to be a `useSyncExternalStore`
 * over a mutable module store, because the value was fetched after mount and
 * had to re-render the tree when it landed. Nothing arrives late any more, so
 * the provider is a plain context over a value that never changes.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { getBrandSnapshot } from '../client.js'
import type { BrandSnapshot } from '../snapshot.js'
import { formatPageTitle, formatRootTitle } from '../page-title.js'

export interface BrandContextValue {
  brand: BrandSnapshot
  /** Convenience: `identity.productName`, the single most-used brand string. */
  productName: string
  /** `'Settings · Papan'`. Omit `page` for the root title. */
  title: (page?: string | null) => string
}

const BrandContext = createContext<BrandContextValue | null>(null)

export function BrandProvider({ children }: { children: ReactNode }) {
  const brand = getBrandSnapshot()

  const value = useMemo<BrandContextValue>(() => ({
    brand,
    productName: brand.identity.productName,
    title: (page) => (page ? formatPageTitle(brand, page) : formatRootTitle(brand)),
  }), [brand])

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
}

/**
 * Read the active brand.
 *
 * Unlike `useTheme`, this does NOT throw when used outside its provider — it
 * falls back to the module store, which always holds a valid snapshot. Brand
 * consumers are spread across the SPA including surfaces that render outside
 * the main tree (portals, modals, the share-view chrome), and a throw there
 * would turn a cosmetic problem into a blank screen. The trade-off is that a
 * missing provider degrades silently to correct-but-unsubscribed values, which
 * is the right failure mode for branding.
 */
export function useBrand(): BrandContextValue {
  const ctx = useContext(BrandContext)
  if (ctx) return ctx
  const brand = getBrandSnapshot()
  return {
    brand,
    productName: brand.identity.productName,
    title: (page) => (page ? formatPageTitle(brand, page) : formatRootTitle(brand)),
  }
}
